
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DocumentContentEditor } from "./DocumentContentEditor";

interface RiskMitigationViewProps {
  content: any;
  documentId: string;
  onUpdate: () => void;
}

interface Risk {
  id: string;
  category: string;
  description: string;
  probability: string;
  impact: string;
  mitigation: string;
  owner: string;
  timeline: string;
}

export function RiskMitigationView({ content, documentId, onUpdate }: RiskMitigationViewProps) {
  const [risks, setRisks] = useState<Risk[]>(content.risks || []);
  const { toast } = useToast();

  const addRisk = () => {
    const newRisk: Risk = {
      id: Date.now().toString(),
      category: "",
      description: "",
      probability: "",
      impact: "",
      mitigation: "",
      owner: "",
      timeline: ""
    };
    const updatedRisks = [...risks, newRisk];
    setRisks(updatedRisks);
    updateContent({ risks: updatedRisks });
  };

  const updateRisk = (id: string, field: keyof Risk, value: string) => {
    const updatedRisks = risks.map(risk => 
      risk.id === id ? { ...risk, [field]: value } : risk
    );
    setRisks(updatedRisks);
    updateContent({ risks: updatedRisks });
  };

  const removeRisk = (id: string) => {
    const updatedRisks = risks.filter(risk => risk.id !== id);
    setRisks(updatedRisks);
    updateContent({ risks: updatedRisks });
  };

  const updateContent = async (updates: any) => {
    try {
      const updatedContent = { ...content, ...updates };
      
      const { error } = await supabase
        .from("documents")
        .update({ 
          content: updatedContent,
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error("Error updating risk mitigation plan:", error);
      toast({
        title: "Error",
        description: "Failed to update risk mitigation plan. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleContentUpdate = (field: string) => (newContent: string) => {
    updateContent({ [field]: newContent });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="h-6 w-6 text-red-600" />
        <h2 className="text-2xl font-bold">Risk Mitigation Plan</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Executive Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentContentEditor
            section="Executive Summary"
            content={content.executiveSummary || ""}
            documentId={documentId}
            field="executiveSummary"
            onUpdate={handleContentUpdate("executiveSummary")}
            showHint={true}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Risk Register</CardTitle>
            <Button onClick={addRisk} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Risk
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {risks.map((risk) => (
            <Card key={risk.id} className="p-4">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-semibold">Risk #{risks.indexOf(risk) + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRisk(risk.id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`category-${risk.id}`}>Risk Category</Label>
                  <Input
                    id={`category-${risk.id}`}
                    placeholder="e.g., Operational, Financial, Strategic"
                    value={risk.category}
                    onChange={(e) => updateRisk(risk.id, 'category', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`probability-${risk.id}`}>Probability</Label>
                  <Input
                    id={`probability-${risk.id}`}
                    placeholder="e.g., High, Medium, Low"
                    value={risk.probability}
                    onChange={(e) => updateRisk(risk.id, 'probability', e.target.value)}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Risk Description</Label>
                  <DocumentContentEditor
                    section={`Risk ${risks.indexOf(risk) + 1} Description`}
                    content={risk.description || ""}
                    documentId={documentId}
                    field={`risk_description_${risk.id}`}
                    onUpdate={(newContent) => updateRisk(risk.id, 'description', newContent)}
                    showHint={true}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`impact-${risk.id}`}>Impact Level</Label>
                  <Input
                    id={`impact-${risk.id}`}
                    placeholder="e.g., High, Medium, Low"
                    value={risk.impact}
                    onChange={(e) => updateRisk(risk.id, 'impact', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`owner-${risk.id}`}>Risk Owner</Label>
                  <Input
                    id={`owner-${risk.id}`}
                    placeholder="Responsible person/department"
                    value={risk.owner}
                    onChange={(e) => updateRisk(risk.id, 'owner', e.target.value)}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Mitigation Strategy</Label>
                  <DocumentContentEditor
                    section={`Risk ${risks.indexOf(risk) + 1} Mitigation`}
                    content={risk.mitigation || ""}
                    documentId={documentId}
                    field={`risk_mitigation_${risk.id}`}
                    onUpdate={(newContent) => updateRisk(risk.id, 'mitigation', newContent)}
                    showHint={true}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`timeline-${risk.id}`}>Timeline</Label>
                  <Input
                    id={`timeline-${risk.id}`}
                    placeholder="Implementation timeline"
                    value={risk.timeline}
                    onChange={(e) => updateRisk(risk.id, 'timeline', e.target.value)}
                  />
                </div>
              </div>
            </Card>
          ))}
          
          {risks.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No risks added yet. Click "Add Risk" to get started.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Monitoring & Review</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentContentEditor
            section="Monitoring & Review"
            content={content.monitoringPlan || ""}
            documentId={documentId}
            field="monitoringPlan"
            onUpdate={handleContentUpdate("monitoringPlan")}
            showHint={true}
          />
        </CardContent>
      </Card>
    </div>
  );
}
